import { Box, Typography } from "@mui/material";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router";
import { useQuestion } from "../context/QuestionContext";

const NAVY = "#0d2535";
const TEAL = "#4abcc9";
const TEAL2 = "#3daab5";
const BORDER = "#d6edf2";
const BG = "#f0f7f9";
const maxPageWidth = "900px";

const steps = [
    {
        icon: "mark_email_read",
        title: "Correo de confirmación",
        desc: "Te enviamos un correo para confirmar que tu pregunta ha sido recibida correctamente.",
    },
    {
        icon: "schedule",
        title: "Revisión médica",
        desc: "Un médico certificado revisará tu caso y preparará una respuesta personalizada.",
    },
    {
        icon: "forum",
        title: "Contacto del médico",
        desc: "Cuando el médico esté listo, recibirás un segundo correo con el enlace para unirte al chat.",
    },
];

export default function ThankYouPage() {
    const navigate = useNavigate();
    const { questionCreated } = useQuestion();

    useEffect(() => {
        if (!questionCreated) navigate("/home");
        window.scrollTo({ left: 0, top: 0, behavior: "smooth" });
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BG, fontFamily: "'Manrope',sans-serif" }}>

            {/* ── HERO ── */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${NAVY} 0%, #0f3347 60%, #143d56 100%)`,
                    position: "relative",
                    overflow: "hidden",
                    pt: { xs: 8, md: 10 },
                    pb: { xs: 8, md: 10 },
                    px: { xs: 2, md: 4 },
                }}
            >
                {/* Dot grid background */}
                <Box
                    sx={{
                        position: "absolute", inset: 0,
                        backgroundImage: `radial-gradient(circle, rgba(74,188,201,.18) 1px, transparent 1px)`,
                        backgroundSize: "28px 28px",
                        pointerEvents: "none",
                    }}
                />

                <Box sx={{ maxWidth: maxPageWidth, mx: "auto", textAlign: "center", position: "relative", zIndex: 2 }}>
                    {/* Check icon */}
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "22px",
                            background: "rgba(74,188,201,.15)",
                            border: "1.5px solid rgba(74,188,201,.35)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mx: "auto",
                            mb: 3,
                        }}
                    >
                        <span
                            className="material-symbols-outlined"
                            style={{
                                fontSize: 40,
                                color: TEAL,
                                lineHeight: 1,
                                fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 48",
                            }}
                        >
                            check_circle
                        </span>
                    </Box>

                    {/* Eyebrow */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.25, mb: 2 }}>
                        <Box sx={{ width: 28, height: 3, background: TEAL, borderRadius: 2 }} />
                        <Typography
                            sx={{
                                color: TEAL,
                                fontFamily: "'Montserrat', sans-serif",
                                fontSize: ".72rem",
                                fontWeight: 700,
                                letterSpacing: "2px",
                                textTransform: "uppercase",
                            }}
                        >
                            Pregunta recibida
                        </Typography>
                        <Box sx={{ width: 28, height: 3, background: TEAL, borderRadius: 2 }} />
                    </Box>

                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "clamp(2rem, 4vw, 3rem)",
                            fontWeight: 900,
                            color: "#fff",
                            lineHeight: 1.1,
                            mb: 2,
                            letterSpacing: "-1px",
                        }}
                    >
                        ¡Tu consulta está en camino!
                    </Typography>

                    <Typography
                        sx={{
                            color: "rgba(255,255,255,.65)",
                            fontSize: "1rem",
                            lineHeight: 1.7,
                            maxWidth: 520,
                            mx: "auto",
                        }}
                    >
                        Hemos recibido tu pregunta con éxito. Un médico certificado la revisará y te contactará pronto.
                    </Typography>
                </Box>
            </Box>

            {/* ── STEPS CARD ── */}
            <Box sx={{ maxWidth: maxPageWidth, mx: "auto", px: { xs: 2, md: 3 }, py: 6 }}>
                <Box
                    sx={{
                        background: "#fff",
                        borderRadius: "20px",
                        border: `1.5px solid ${BORDER}`,
                        overflow: "hidden",
                    }}
                >
                    {/* Card header */}
                    <Box
                        sx={{
                            background: `linear-gradient(90deg, rgba(74,188,201,.07), transparent)`,
                            borderBottom: `1.5px solid ${BORDER}`,
                            px: 3,
                            py: 2.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                width: 38, height: 38, borderRadius: "10px",
                                background: "rgba(74,188,201,.12)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0, overflow: "hidden",
                            }}
                        >
                            <span
                                className="material-symbols-outlined"
                                style={{ fontSize: 20, color: TEAL, lineHeight: 1 }}
                            >
                                list_alt
                            </span>
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: ".95rem",
                                    fontWeight: 700,
                                    color: NAVY,
                                }}
                            >
                                ¿Qué sigue ahora?
                            </Typography>
                            <Typography sx={{ fontSize: ".78rem", color: "#6b7280", mt: 0.25 }}>
                                Estos son los pasos que ocurrirán tras enviar tu consulta
                            </Typography>
                        </Box>
                    </Box>

                    {/* Steps */}
                    <Box sx={{ px: 3, py: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                        {steps.map((step, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 2,
                                    p: 2.5,
                                    borderRadius: "14px",
                                    border: `1.5px solid ${BORDER}`,
                                    background: BG,
                                }}
                            >
                                {/* Number + icon */}
                                <Box sx={{ flexShrink: 0, position: "relative" }}>
                                    <Box
                                        sx={{
                                            width: 48, height: 48, borderRadius: "14px",
                                            background: `rgba(74,188,201,.12)`,
                                            border: `1.5px solid rgba(74,188,201,.25)`,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}
                                    >
                                        <span
                                            className="material-symbols-outlined"
                                            style={{
                                                fontSize: 24,
                                                color: TEAL,
                                                lineHeight: 1,
                                                fontVariationSettings: "'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 48",
                                            }}
                                        >
                                            {step.icon}
                                        </span>
                                    </Box>
                                    {/* Step number badge */}
                                    <Box
                                        sx={{
                                            position: "absolute", top: -6, right: -6,
                                            width: 20, height: 20, borderRadius: "50%",
                                            background: TEAL, border: "2px solid #fff",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}
                                    >
                                        <Typography sx={{ fontSize: ".65rem", fontWeight: 800, color: "#fff", lineHeight: 1 }}>
                                            {i + 1}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Montserrat', sans-serif",
                                            fontSize: ".9rem",
                                            fontWeight: 700,
                                            color: NAVY,
                                            mb: 0.5,
                                        }}
                                    >
                                        {step.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: ".83rem",
                                            color: "#6b7280",
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {step.desc}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* Footer CTA */}
                    <Box
                        sx={{
                            borderTop: `1.5px solid ${BORDER}`,
                            px: 3,
                            py: 3,
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            background: `linear-gradient(90deg, rgba(74,188,201,.04), transparent)`,
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontSize: ".85rem",
                                    fontWeight: 600,
                                    color: NAVY,
                                }}
                            >
                                Revisa tu bandeja de entrada
                            </Typography>
                            <Typography sx={{ fontSize: ".78rem", color: "#6b7280", mt: 0.25 }}>
                                También revisa tu carpeta de spam por si acaso
                            </Typography>
                        </Box>
                        <Link to="/home" style={{ textDecoration: "none" }}>
                            <Box
                                component="button"
                                sx={{
                                    background: `linear-gradient(135deg, ${TEAL}, ${TEAL2})`,
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "12px",
                                    px: 3,
                                    py: 1.5,
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 700,
                                    fontSize: ".88rem",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    transition: "all 0.2s",
                                    whiteSpace: "nowrap",
                                    "&:hover": {
                                        background: `linear-gradient(135deg, ${TEAL2}, #2d8f9a)`,
                                        transform: "translateY(-1px)",
                                        boxShadow: "0 4px 12px rgba(74,188,201,.35)",
                                    },
                                }}
                            >
                                <span
                                    className="material-symbols-outlined"
                                    style={{ fontSize: 18, lineHeight: 1 }}
                                >
                                    home
                                </span>
                                Volver al inicio
                            </Box>
                        </Link>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
